import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../../context/JourneyContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Calendar, Mail, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const NextStep = () => {
  const navigate = useNavigate();
  const { journey, trackPageView, submitInquiry, trackCtaClick } = useJourney();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    organisation: '',
    email: '',
    location: '',
    preferred_term: '',
    message: ''
  });

  React.useEffect(() => {
    trackPageView('portal_next_step');
  }, [trackPageView]);

  const cta = journey?.recommended_cta || 'Book a kōrero';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await submitInquiry({
        ...formData,
        inquiry_type: 'booking'
      });
      trackCtaClick('inquiry_submitted');
      toast.success('Enquiry sent! We\'ll be in touch soon.');
      navigate('/portal/continue-journey');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div 
      data-testid="next-step-screen"
      className="min-h-screen relative overflow-hidden bg-[#1C1917]"
    >
      {/* Background */}
      <div className="absolute top-40 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        {/* Navigation */}
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/portal/options')}
          className="mb-8 text-stone-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          <div className="h-1 w-8 rounded-full bg-primary" />
          <div className="h-1 w-8 rounded-full bg-primary" />
          <div className="h-1 w-8 rounded-full bg-primary" />
          <div className="h-1 w-8 rounded-full bg-primary" />
          <span className="ml-4 text-sm text-stone-400">Step 4 of 4</span>
        </div>

        {/* Header */}
        <div className="text-center mb-10 animate-fade-in-up">
          <h1 
            data-testid="next-step-title"
            className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4"
          >
            Take the Next Step
          </h1>
          <p className="text-base sm:text-lg text-stone-400">
            Ready to begin? Share your details and we'll be in touch.
          </p>
          <p className="text-sm text-stone-500 mt-2">
            Email: <a href="mailto:info@letssiva.com" className="text-primary hover:underline">info@letssiva.com</a>
          </p>
        </div>

        {/* Form Card */}
        <Card className="bg-stone-900/80 border border-stone-800 rounded-3xl p-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-stone-300">Your Name *</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  placeholder="e.g., Sarah Mitchell"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="rounded-xl h-12 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="organisation" className="text-stone-300">Organisation *</Label>
                <Input
                  id="organisation"
                  data-testid="organisation-input"
                  placeholder="e.g., Westside Primary School"
                  value={formData.organisation}
                  onChange={(e) => handleChange('organisation', e.target.value)}
                  required
                  className="rounded-xl h-12 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-stone-300">Email *</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="sarah@school.nz"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  className="rounded-xl h-12 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="location" className="text-stone-300">Location</Label>
                <Input
                  id="location"
                  data-testid="location-input"
                  placeholder="e.g., Auckland"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  className="rounded-xl h-12 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="term" className="text-stone-300">Preferred Term</Label>
              <Select 
                value={formData.preferred_term} 
                onValueChange={(value) => handleChange('preferred_term', value)}
              >
                <SelectTrigger data-testid="term-select" className="rounded-xl h-12 bg-stone-800 border-stone-700 text-stone-300">
                  <SelectValue placeholder="When would you like to start?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Term 3 2026">Term 3 2026</SelectItem>
                  <SelectItem value="Term 4 2026">Term 4 2026</SelectItem>
                  <SelectItem value="Term 1 2027">Term 1 2027</SelectItem>
                  <SelectItem value="Term 2 2027">Term 2 2027</SelectItem>
                  <SelectItem value="Term 3 2027">Term 3 2027</SelectItem>
                  <SelectItem value="Term 4 2027">Term 4 2027</SelectItem>
                  <SelectItem value="Flexible">Flexible / Not sure</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-stone-300">Message (Optional)</Label>
              <Textarea
                id="message"
                data-testid="message-input"
                placeholder="Tell us anything else we should know..."
                value={formData.message}
                onChange={(e) => handleChange('message', e.target.value)}
                className="rounded-xl min-h-[100px] bg-stone-800 border-stone-700 text-white placeholder:text-stone-500"
              />
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              data-testid="submit-btn"
              disabled={loading || !formData.name || !formData.organisation || !formData.email}
              size="lg"
              className="w-full rounded-full py-6 text-lg font-bold bg-primary hover:bg-primary/90 text-white transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5 mr-2" />
                  {cta}
                </>
              )}
            </Button>
          </form>
        </Card>

        {/* Alternative actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            data-testid="download-overview-btn"
            variant="outline"
            onClick={() => trackCtaClick('download_overview')}
            className="rounded-full border-stone-700 text-stone-300 hover:border-primary/50 hover:bg-stone-800"
          >
            <FileText className="w-4 h-4 mr-2" />
            Download overview
          </Button>
          <Button
            data-testid="explore-funding-btn"
            variant="outline"
            onClick={() => trackCtaClick('explore_funding')}
            className="rounded-full border-stone-700 text-stone-300 hover:border-primary/50 hover:bg-stone-800"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Explore funding options
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NextStep;
